import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import SkeletonWrapper from '../../../../shared/components/skeleton/SkeletonWrapper';
import { Accordion, AccordionDetails, AccordionSummary, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GET_APPRAISAL_BY_SEARCH_TEXT from '../../shared/queries/getAppraisalsBySearchTextQuery';
import ResponsiveTabMenu from '@shared/components/ResponsiveTabMenu';
import useUriFragment from '@shared/hooks/useUriFragment';
import AppraiserAppraisalsTabTables from './AppraiserAppraisalsTabTables';
import ApolloError from '@shared/components/ApolloError';
import AppraiserDataMapper from '../../../../AppraiserForm/mappers/AppraiserDataMapper';
import AppraiserAppraisalsRemoveContainer from './AppraiserAppraisalsRemoveContainer';
import { handleEnterKeyDown } from '@shared/services/getOnEnterPressedHandler';
import { sortBy } from 'lodash';

/**
 * @typedef {object} AppraiserAppraisalAssignmentSectionProps
 * @property {Array<*>} [assignedAppraisals]
 * @property {Array<*>} [autoAssignedAppraisals]
 * @property {(appraiserFormData: Object | ((prev: Object) => Object)) => void} [setAppraiserFormData]
 * @property {boolean} [loading]
 */

/**
 * @param {AppraiserAppraisalAssignmentSectionProps} props
 */
const AppraiserAppraisalAssignmentSection = props => {
  const {
    setAppraiserFormData,
    loading,
    assignedAppraisals = [],
    autoAssignedAppraisals = []
  } = props;

  const [searchCurrentPage, setSearchCurrentPage] = useState(1);

  const [sectionVisibility, setSectionVisibility] = useState(true);
  const [appraisalSearchText, setAppraisalSearchText] = useState(null);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedAppraisalToUnassign, setSelectedAppraisalToUnassign] = useState(null);

  const [
    getSearchedAppraisals,
    { data: searchedAppraisalsData, loading: searchLoading, error: searchError }
  ] = useLazyQuery(GET_APPRAISAL_BY_SEARCH_TEXT);

  const [activeTab, _, _1, reactiveTabWithLocationState] = useUriFragment(tabs);

  const searchedAppraisalsCount = searchedAppraisalsData
    ? searchedAppraisalsData.searchAppraisalsCount
    : 0;
  const searchedAppraisals = searchedAppraisalsData
    ? AppraiserDataMapper.searchedAppraisalsToView(searchedAppraisalsData.searchAppraisals)
    : [];

  const changeSectionVisibility = () => {
    setSectionVisibility(prevState => !prevState);
  };

  const handleTabChange = newTab => {
    reactiveTabWithLocationState(newTab);
  };

  const handleSearchBtnClick = async () => {
    if (!appraisalSearchText) {
      return;
    }

    handleTabChange(TAB_TYPES.Search.value);
    setSearchCurrentPage(1);

    await getSearchedAppraisals({
      variables: {
        filter: appraisalSearchText,
        offset: initialOffset,
        limit: tableRowSize
      }
    });
  };

  const fetchAppraisalsByFilterData = async offset => {
    setSearchCurrentPage((offset + tableRowSize) / tableRowSize);

    await getSearchedAppraisals({
      variables: {
        filter: appraisalSearchText,
        offset: offset,
        limit: tableRowSize
      }
    });
  };

  const handleAssignAppraisal = (e, appraisalId) => {
    e.preventDefault();

    const appraisal = searchedAppraisals.find(x => x.id === appraisalId);

    const updatedAppraisals = [
      ...assignedAppraisals,
      {
        id: appraisal.id,
        name: appraisal.name,
        orgUnitNames: appraisal.orgUnitNames
      }
    ];

    setAppraiserFormData(prev => ({
      ...prev,
      assignedAppraisals: sortBy(updatedAppraisals, [x => x.name, x => x.id])
    }));
  };

  const handleOpenDialog = (e, appraisalId) => {
    e.preventDefault();

    setSelectedAppraisalToUnassign(appraisalId);
    setOpenConfirmDialog(true);
  };

  if (searchError) {
    return <ApolloError />;
  }

  return (
    <>
      <Accordion
        expanded={sectionVisibility}
        className='atl-appraisal-assignment'
        slotProps={{
          transition: { unmountOnExit: true }
        }}>
        <AccordionSummary
          onClick={changeSectionVisibility}
          expandIcon={<ExpandMoreIcon data-testid='atl-appraisal-assignment-icon' />}>
          <h3>ATL Assignment</h3>
        </AccordionSummary>
        <AccordionDetails className='atl-assignment-content'>
          <div className='section-description'>{description}</div>
          <SkeletonWrapper show={loading}>
            <div className='input-group' data-testid='input-group'>
              <input
                className='input-field'
                type='text'
                placeholder='Search by OU Name or Appraisal ID.'
                onChange={e => setAppraisalSearchText(e.target.value)}
                onKeyDown={e => handleEnterKeyDown(e, handleSearchBtnClick)}
              />
              <Button className='primary-button sm' onClick={handleSearchBtnClick} type='button'>
                Search
              </Button>
            </div>
            <ResponsiveTabMenu activeTab={activeTab} tabs={tabs} onChange={handleTabChange} />
            <AppraiserAppraisalsTabTables
              searchedAppraisals={searchedAppraisals}
              searchedAppraisalsCount={searchedAppraisalsCount}
              assignedAppraisals={assignedAppraisals}
              autoAssignedAppraisals={autoAssignedAppraisals}
              handleAssignClick={handleAssignAppraisal}
              handleOpenConfirmDialog={handleOpenDialog}
              handlePageChange={fetchAppraisalsByFilterData}
              pageSize={tableRowSize}
              searchCurrentPage={searchCurrentPage}
              activeTab={activeTab}
              loading={searchLoading}
            />
          </SkeletonWrapper>
        </AccordionDetails>
      </Accordion>
      <AppraiserAppraisalsRemoveContainer
        assignedAppraisals={assignedAppraisals}
        openConfirmDialog={openConfirmDialog}
        setOpenConfirmDialog={setOpenConfirmDialog}
        selectedAppraisalToUnassign={selectedAppraisalToUnassign}
        setSelectedAppraisalToUnassign={setSelectedAppraisalToUnassign}
        setAppraiserFormData={setAppraiserFormData}
      />
    </>
  );
};

const tableRowSize = 10;
const initialOffset = 0;

const description =
  'Enter an OU name or appraisal ID in the text field ' +
  'and click the “Search” button to view matches in the “Results” tab. ' +
  'Click the “Assign” action text to give the ATL access to an appraisal. ' +
  'Click the “Unassign” action text to remove ATL access to an appraisal. ' +
  'If the appraisal was auto-assigned by CAS, then no action text will display. ' +
  'Alternatively, click the “Manual” tab to view which appraisals have already been assigned ' +
  'to the ATL by ISACA Admins, or the “Auto” tab to view appraisals that were auto-assigned to the ATL by CAS.';

const TAB_TYPES = {
  Search: {
    value: 'search',
    displayName: 'Results'
  },
  Manual: {
    value: 'manual',
    displayName: 'Manual'
  },
  Auto: {
    value: 'auto',
    displayName: 'Auto'
  }
};

const tabs = [
  {
    value: TAB_TYPES.Search.value,
    displayName: TAB_TYPES.Search.displayName
  },
  {
    value: TAB_TYPES.Manual.value,
    displayName: TAB_TYPES.Manual.displayName
  },
  {
    value: TAB_TYPES.Auto.value,
    displayName: TAB_TYPES.Auto.displayName
  }
];

export default AppraiserAppraisalAssignmentSection;
